import { Injectable } from '@angular/core';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
  NgbDatepicker,
  NgbDatepickerKeyboardService,
  NgbDateStruct,
  NgbDropdown,
  NgbInputDatepicker,
  NgbDatepickerI18n
} from '@ng-bootstrap/ng-bootstrap';
import * as Moment from 'moment';
import {
  DateObject,
  DatePickerOptions,
  DropdownType,
  outsideDaysType
} from './datepicker.model';
import {
  isNumber,
  MyFormatter,
  padNumber,
  toInteger
} from './datepicker.service';
import { CustomKeyboardService } from './datepicker-keyboard.service';
import { TranslateService } from '@ngx-translate/core';

const moment: any = (Moment as any).default || Moment;

const I18N_VALUES = {
  en: {
    months: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ],
    weekdays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    weekLabel: 'week'
  },
  ga: {
    months: [
      'Eanáir',
      'Feabhra',
      'Márta',
      'Aibreán',
      'Bealtaine',
      'Meitheamh',
      'Iúil',
      'Lúnasa',
      'Meán Fómhair',
      'Deireadh Fómhair',
      'Samhain',
      'Nollaig'
    ],
    weekdays: ['Lu', 'Má', 'Cé', 'Dé', 'Ao', 'Sa', 'Do'],
    weekLabel: 'seachtain'
  }
};

@Injectable()
export class CustomDatepickerI18n extends NgbDatepickerI18n {
  constructor(private translate: TranslateService) {
    super();
  }

  private get language(): string {
    return this.translate.currentLang || this.translate.defaultLang;
  }

  getWeekdayShortName(weekday: number): string {
    return I18N_VALUES[this.language].weekdays[weekday - 1];
  }

  getWeekdayLabel(weekday: number): string {
    return I18N_VALUES[this.language].weekdays[weekday - 1];
  }

  getWeekLabel(): string {
    return I18N_VALUES[this.language].weekLabel;
  }

  getMonthShortName(month: number): string {
    return I18N_VALUES[this.language].months[month - 1];
  }

  getMonthFullName(month: number): string {
    return this.getMonthShortName(month);
  }

  getDayAriaLabel(date: NgbDateStruct): string {
    return moment(`${date.day}/${date.month}/${date.year}`, 'D/M/YYYY').format('MMMM Do YYYY');
  }
}

enum KeyPress {
  Tab = 9,
  Enter = 13,
  Esc = 27,
  Space = 32,
  Up = 38,
  Down = 40
}

@Component({
  selector: 'oir-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.less'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true
    },
    { provide: NgbDateParserFormatter, useClass: MyFormatter },
    { provide: NgbDatepickerKeyboardService, useClass: CustomKeyboardService },
    { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n }
  ]
})
export class DatePickerComponent
  implements OnInit, OnChanges, ControlValueAccessor
{
  formats = ['D/MM/YYYY', 'DD/MM/YYYY', 'D/M/YYYY', 'DD/M/YYYY'];
  @ViewChild(NgbInputDatepicker) datepickerInput: NgbInputDatepicker;
  @ViewChild(NgbDatepicker) datepicker: NgbDatepicker;
  @ViewChild('dpInput') input: ElementRef;
  @ViewChild('monthDD') monthDD: NgbDropdown;
  @ViewChildren('monthInputs') monthInputs: QueryList<ElementRef>;
  @ViewChild('yearDD') yearDD: NgbDropdown;
  @ViewChildren('yearInputs') yearInputs: QueryList<ElementRef>;
  @ViewChild('toggleButton') toggleButton: ElementRef;
  @ViewChild('mainDatepickerDiv') mainDatepickerDiv: ElementRef;

  @HostListener('document:click', ['$event'])
  // if clicked outside the datepicker then close it (if open)
  clickOut(event: MouseEvent) {
    if (this.showDatePicker) {
      if (!this.mainDatepickerDiv.nativeElement.contains(event.target)) {
        this.closeDatepicker();
      }
    }
  }

  @Input() model: any = null;
  @Input() rangeModel: any;
  @Input() disabled: boolean;
  @Input() invalid: boolean;
  @Input() placeholder: string;
  @Input() daterangeplaceholder: string;
  @Input() options: DatePickerOptions = {};
  @Input() showNoOfMonths = 1;
  @Input() showWeekNumbers = false;
  @Input() startDate;
  @Input() outsideDays: outsideDaysType = 'visible';
  @Input() dateRange = false;
  @Input() hasTranslations = false;

  @Output() readonly modelChange: EventEmitter<string> =
    new EventEmitter<string>();

  currentDateView: any = {};
  previousDateValue: DateObject | null;
  active = false;
  showDatePicker = false;
  numbers: Array<number> = new Array(30);
  showDropDownYear: boolean;
  showDropDownMonth: boolean;
  hoveredDate: NgbDate;
  fromDate: NgbDate | null;
  toDate: NgbDate | null;
  previousDateRangeValue: any;
  DropdownType = DropdownType;
  private listener: () => void;
  propagateChange = (_: string) => {};
  propagateTouch = (_?: any) => {};

  constructor(
    private calendar: NgbCalendar,
    private el: ElementRef,
    private renderer: Renderer2,
    private translate: TranslateService,
    private i18n: NgbDatepickerI18n
  ) {
    this.numbers.fill(0);
    this.numbers = this.numbers.map((x, i) => i);
  }

  ConvertStringToNumber(value): number {
    return Number(value);
  }

  ngOnInit(): void {
    if (!this.placeholder || !this.daterangeplaceholder) {
      this.placeholder = 'DD/MM/YYYY';
      this.daterangeplaceholder = 'DD/MM/YYYY - DD/MM/YYYY';
    }
    if (this.model || this.rangeModel) {
      this.initModel();
    } else {
      this.model = null;
    }

    this.previousDateValue = this.model ? this.model : this.rangeModel;

    this.setInitialCurrentDateView();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.model && !changes.model.firstChange) {
      this.initModel(changes.model);
    } else if (changes.rangeModel && !changes.rangeModel.firstChange) {
      this.initModel(changes.rangeModel);
    }
  }

  // set rangeModel on init if input is a date range
  getDateRange(rangeModel, modelChanges, modelFormat): string {
    const date = this.rangeModel.split('-');
    const fromDateModel = moment(date[0], modelFormat);
    const toDateModel = moment(date[1], modelFormat);

    if (modelChanges) {
      return rangeModel.isValid()
        ? this.toDateString([
            this.toDateObj(fromDateModel.format('DD/MM/YYYY')),
            this.toDateObj(toDateModel.format('DD/MM/YYYY'))
          ])
        : moment().format('DD/MM/YYYY');
    }
  }

  getInputAriaLabel(inputValue: string): string {
    const selectedDate =
      inputValue && inputValue !== ''
        ? `, ${this.translate.instant('CALENDAR.DATE_SELECTED')} ${inputValue}`
        : '';

    return `${this.translate.instant('CALENDAR.SELECT_DATE')} ${
      selectedDate ? selectedDate : ''
    }`;
  }

  toggle(): void {
    this.showDatePicker = !this.showDatePicker;
    if (this.showDatePicker) {
      this.registerListener();
      if (this.model != null && !this.dateRange) {
        setTimeout(() => {
          this.datepicker.navigateTo(this.model);
        });
      } else if (this.rangeModel != null && this.dateRange) {
        this.model = this.startDate;
      }
    }
  }

  dropdownOpenChange(openChange: boolean, dropdownType: string): void {
    if (!openChange) {
      if (dropdownType === DropdownType.Month) {
        this.showDropDownMonth = false;
      } else if (dropdownType === DropdownType.Year) {
        this.showDropDownYear = false;
      }
    }
  }

  keyToggleDatepicker(keyEvent: KeyboardEvent): void {
    if (
      keyEvent.which === KeyPress.Enter ||
      keyEvent.which === KeyPress.Space
    ) {
      keyEvent.preventDefault();
      this.toggle();
      if (this.showDatePicker) {
        this.autoFocusDay();
      }
    } else if (keyEvent.which === KeyPress.Tab && this.showDatePicker) {
      this.autoFocusDay();
    }
  }

  autoFocusDay(): void {
    setTimeout(() => {
      const findFocusableDay = Array.from(
        document.getElementsByClassName('ngb-dp-day')
      ).find(d => (d as HTMLElement).tabIndex === 0) as HTMLElement;
      if (findFocusableDay) {
        findFocusableDay.focus();
      }
    });
  }

  focusPreviousButton(event: KeyboardEvent): void {
    if (!event.shiftKey) {
      event.preventDefault();
      const button = document.getElementById('previous-month-button');
      button.focus();
    }
  }

  registerOnChange(fn): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn): void {
    this.propagateTouch = fn;
  }

  writeValue(obj: any): void {
    // Convert string to NgbDateStruct object.
    if (obj === null || obj === undefined) {
      this.resetInput();
    } else if (obj.value) {
      if (this.dateRange) {
        this.updateDateRangeValue(obj.value);
        this.previousDateRangeValue = obj.value;
      } else {
        this.model = this.toDateObj(obj.value);
        this.previousDateValue = this.toDateObj(obj.value);
      }
    } else {
      if (!this.options.readonly) {
        if (this.dateRange) {
          this.updateDateRangeValue(obj);
          this.previousDateRangeValue = obj;
        } else {
          this.model = this.toDateObj(obj);
          this.previousDateValue = this.toDateObj(obj);
        }
      }
    }
  }

  setDisabledState(value: any): void {
    this.disabled = value;
  }

  // Allow select date from outside component.
  select(data): void {
    // eslint-disable-next-line no-prototype-builtins
    const datePassed = data && data.value ? data.value : data;
    if (!this.dateIsNotInRange(datePassed)) {
      this.model = this.toDateObj(datePassed);
      this.startDate = null;
      this.previousDateValue = this.toDateObj(datePassed);
      this.propagateChange(datePassed);
      this.modelChange.emit(datePassed);
    } else {
      this.input.nativeElement.value = '';
    }
  }

  keyUpCircle(event: KeyboardEvent): void {
    // If escaped pressed while focused on the navigation arrows close the datepicker.
    if (event.which === KeyPress.Esc) {
      this.closeDatepicker(true);
    }
  }

  keyDownCircle(event: KeyboardEvent): void {
    if (event.which === KeyPress.Tab && event.shiftKey) {
      event.preventDefault();
      this.autoFocusDay();
    }
  }

  closeDatepicker(focus?: boolean): void {
    this.showDatePicker = false;
    this.showDropDownMonth = false;
    this.showDropDownYear = false;
    if (focus) {
      this.toggleButton.nativeElement.focus();
    }
  }

  resetInput(): void {
    this.model = null;
    this.rangeModel = null;
    this.startDate = null;
    this.fromDate = null;
    this.toDate = null;
    this.previousDateValue = null;
    this.propagateChange(this.model);
    this.modelChange.emit(this.model);
    this.propagateChange(this.rangeModel);
    this.modelChange.emit(this.rangeModel);
  }

  keyupEventHandler(keyCode: number): void {
    // If escaped pressed while focused on input textbox close the datepicker and reset the value.
    if (keyCode === KeyPress.Esc) {
      this.model = this.previousDateValue;
      this.startDate = null;
      this.fromDate = null;
      this.toDate = null;
      this.closeDatepicker();
      this.input.nativeElement.value = '';
    }
  }

  clearInput(event?: MouseEvent): void {
    this.resetInput();
    this.clearValues();
    if (event) {
      event.stopPropagation();
    }
  }

  keyClearInput(event?: MouseEvent): void {
    if (event.which === KeyPress.Enter || event.which === KeyPress.Space) {
      this.clearInput(event);
      event.preventDefault();
    }
  }

  // On blur if date invalid reset to null.
  onBlur($event: FocusEvent): void {
    const inputValue = this.input.nativeElement.value;
    if (this.input.nativeElement.value) {
      if (this.dateRange) {
        this.updateDateRangeValue(inputValue);
      } else {
        this.updateDateValue(inputValue);
      }
      this.propagateTouch($event);
    }
  }

  // Checks input value and sets model if valid.
  updateDateValue(dateValue): any {
    if (
      (dateValue !== '' && !moment(dateValue, this.formats).isValid()) ||
      (dateValue !== '' && this.dateIsNotInRange(dateValue))
    ) {
      this.input.nativeElement.value = '';
      this.model = null;
      this.startDate = null;
      this.propagateChange(this.model);
      this.modelChange.emit(this.model);
      return null;
    } else if (
      dateValue !== '' &&
      this.toDateString(this.previousDateValue) !==
        this.input.nativeElement.value
    ) {
      this.model = this.toDateObj(this.input.nativeElement.value);
      this.propagateChange(this.toDateString(this.model));
      this.modelChange.emit(this.toDateString(this.model));
      return this.model;
    }
  }

  // update daterange only if input is a range
  updateDateRangeValue(dateValue): any {
    let fromDate = '';
    let toDate = '';
    let fromDateNgbDate;
    let toDateNgbDate;
    const dates = dateValue.split('-');
    if (dates.length === 2) {
      fromDate = dates[0];
      toDate = dates[1];
      if (
        !moment(fromDate, this.formats).isValid() ||
        !moment(toDate, this.formats).isValid()
      ) {
        this.clearValues();

        return null;
      } else {
        fromDateNgbDate = this.toNgbdate(this.toDateObj(fromDate));
        toDateNgbDate = this.toNgbdate(this.toDateObj(toDate));
      }
      // check of toDate is after than fromDate
      if (!toDateNgbDate.after(fromDateNgbDate)) {
        this.clearValues();

        return null;
      }
    } else {
      this.clearValues();

      return null;
    }

    if (
      dateValue !== '' &&
      moment(fromDate, this.formats).isValid() &&
      moment(toDate, this.formats).isValid()
    ) {
      this.toDate = toDateNgbDate;
      this.fromDate = fromDateNgbDate;
      this.rangeModel = this.toDateString([this.fromDate, this.toDate]);
      this.propagateChange(this.rangeModel);
      this.modelChange.emit(this.rangeModel);
      if (this.input && (this.previousDateRangeValue instanceof Array
        ? this.toDateString(this.previousDateRangeValue)
        : this.previousDateRangeValue) !== this.input.nativeElement.value) {
        this.renderer.setProperty(
          this.input.nativeElement,
          'value',
          this.rangeModel
        );
      }
      return this.rangeModel;
    } else {
      this.clearValues();
      return null;
    }
  }

  clearValues(): void {
    if (this.input) {
      this.input.nativeElement.value = '';
    }
    this.rangeModel = this.previousDateRangeValue;
    this.fromDate = null;
    this.toDate = null;
    this.propagateChange(this.rangeModel);
    this.modelChange.emit(this.rangeModel);
  }
  // If closed and focused on input, when enter pressed show calender.
  keypress(event: KeyboardEvent, value): void {
    if (
      event.which === KeyPress.Enter &&
      !this.showDatePicker &&
      !this.disabled
    ) {
      event.preventDefault();
      this.showDatePicker = true;
      this.input.nativeElement.focus();
      this.registerListener();
      if (value !== '') {
        // Bootstrap datepicker directive has not been loaded as child uses ngif. So set default startdate.
        if (!this.dateRange && this.updateDateValue(value) !== null) {
          this.startDate = this.model;
        }
        if (this.dateRange && this.updateDateRangeValue(value) !== null) {
          this.startDate = this.fromDate;
        }
      }
      this.autoFocusDay();
    } else if (
      event.which === KeyPress.Enter &&
      this.showDatePicker &&
      value !== ''
    ) {
      // If date is not null navigate to date on calendar.
      if (
        value !== this.toDateString(this.model) &&
        this.updateDateValue(value) !== null
      ) {
        this.datepicker.navigateTo(this.model);
        if (this.dateRange && this.updateDateRangeValue(value) !== null) {
          this.startDate = this.fromDate;
          this.autoFocusDay();
        }
      } else {
        // No change in date so hide datepicker.
        this.closeDatepicker();
      }
    } else if (event.which === KeyPress.Tab && this.showDatePicker) {
      this.autoFocusDay();
    }
  }

  // When date is selected propogate it back up.
  onDateSelect(eventObj): void {
    this.model = eventObj;
    this.propagateChange(this.toDateString(this.model));
    this.modelChange.emit(this.toDateString(this.model));
    this.closeDatepicker();
    this.toggleButton.nativeElement.focus();
  }

  // Navigate the months using the arrow icon in the datepicker header.
  // Go to previous month.
  navigatePrevious(): void {
    const navObj = {
      month:
        this.currentDateView.next.month === 1
          ? 12
          : this.currentDateView.next.month - 1,
      year:
        this.currentDateView.next.month === 1
          ? this.currentDateView.next.year - 1
          : this.currentDateView.next.year
    } as NgbDate;
    this.datepicker.navigateTo(navObj);
  }
  // Go to next month.
  navigateNext(): void {
    const navObj = {
      month:
        this.currentDateView.next.month === 12
          ? 1
          : this.currentDateView.next.month + 1,
      year:
        this.currentDateView.next.month === 12
          ? this.currentDateView.next.year + 1
          : this.currentDateView.next.year
    } as NgbDate;

    this.datepicker.navigateTo(navObj);
  }

  // When month selected from dropdown.
  selectMonthView(monthNo: number): void {
    const navObj = {
      month: monthNo + 1,
      year: this.currentDateView.next.year
    };
    this.datepicker.navigateTo(navObj);
    this.showDropDownYear = false;
    this.showDropDownMonth = false;
  }

  // When year selected from dropdown.
  selectYearView(event): void {
    const navObj = {
      month: this.currentDateView.next.month,
      year: Number(event.target.value)
    };
    this.datepicker.navigateTo(navObj);
    this.showDropDownYear = false;
    this.showDropDownMonth = false;
  }

  calendarNavigated(newDateView): void {
    if (newDateView.current) {
      this.currentDateView = newDateView;
    }
  }

  // Converts to match NgbDateStruct object.
  toDateObj(value): any {
    if (typeof value !== 'undefined' && value !== null && typeof value === 'string') {
      let dateInput = value.trim().replace('-', '/');
      dateInput = moment(dateInput, this.formats).format('DD/MM/YYYY');
      if (dateInput !== 'Invalid date') {
        const year = dateInput.match(/\d{4}/g).map(Number);
        dateInput = dateInput.replace(String(year), '');
        const dayAndMonth = dateInput.match(/\d{2}/g).map(Number);
        return NgbDate.from({
          day: toInteger(dayAndMonth[0]),
          month: toInteger(dayAndMonth[1]),
          year: toInteger(year)
        });
      }
    }
    return null;
  }

  // Handle key press when focused on dropdown of months.
  monthKeyPress(event: KeyboardEvent): void {
    if (event.which === KeyPress.Enter && this.showDropDownMonth) {
      this.showDropDownMonth = false;
      event.preventDefault();
      this.monthDD.close();
    }
    if (
      event.which === KeyPress.Up ||
      event.which === KeyPress.Down ||
      (event.which === KeyPress.Tab && this.showDropDownMonth)
    ) {
      event.preventDefault();
    }
    if (
      event.which === KeyPress.Up ||
      (event.which === KeyPress.Tab && event.shiftKey && this.showDropDownMonth)
    ) {
      if (this.currentDateView.next.month >= 1) {
        const navObj = {
          month:
            this.currentDateView.next.month === 1
              ? 12
              : this.currentDateView.next.month - 1,
          year:
            this.currentDateView.next.month === 1
              ? this.currentDateView.next.year - 1
              : this.currentDateView.next.year
        } as NgbDate;
        this.datepicker.navigateTo(navObj);
        this.scrollToDDItem(DropdownType.Month, false);
      }
    } else if (
      event.which === KeyPress.Down ||
      (event.which === KeyPress.Tab &&
        !event.shiftKey &&
        this.showDropDownMonth)
    ) {
      if (this.currentDateView.next.month <= 12) {
        const navObj = {
          month:
            this.currentDateView.next.month === 12
              ? 1
              : this.currentDateView.next.month + 1,
          year:
            this.currentDateView.next.month === 12
              ? this.currentDateView.next.year + 1
              : this.currentDateView.next.year
        } as NgbDate;
        this.datepicker.navigateTo(navObj);
        this.scrollToDDItem(DropdownType.Month, false);
      }
    } else if (event.which === KeyPress.Esc) {
      if (this.showDropDownMonth) {
        this.showDropDownMonth = false;
      } else {
        this.closeDatepicker(true);
      }
    }
  }

  // Handle key press when focused on dropdown of years.
  yearKeyPress(event: KeyboardEvent): void {
    if (event.which === KeyPress.Enter && this.showDropDownYear) {
      this.showDropDownYear = false;
      event.preventDefault();
      this.yearDD.close();
    }
    if (
      event.which === KeyPress.Up ||
      event.which === KeyPress.Down ||
      (event.which === KeyPress.Tab && this.showDropDownYear)
    ) {
      event.preventDefault();
    }
    if (
      event.which === KeyPress.Up ||
      (event.which === KeyPress.Tab && event.shiftKey && this.showDropDownYear)
    ) {
      const navObj = {
        month: this.currentDateView.next.month,
        year: this.currentDateView.next.year - 1
      } as NgbDate;
      this.datepicker.navigateTo(navObj);
    } else if (
      event.which === KeyPress.Down ||
      (event.which === KeyPress.Tab && !event.shiftKey && this.showDropDownYear)
    ) {
      const navObj = {
        month: this.currentDateView.next.month,
        year: this.currentDateView.next.year + 1
      } as NgbDate;
      this.datepicker.navigateTo(navObj);
    } else if (event.which === KeyPress.Esc) {
      if (this.showDropDownYear) {
        this.showDropDownYear = false;
      } else {
        this.closeDatepicker(true);
      }
    }
  }

  // This is used when opening a header dropdown. It scrolls to the selected item.
  scrollToDDItem(dropdown: string, toggle = true): void {
    if (document.activeElement.tagName.toLowerCase() !== 'input') {
      let scrollVal;
      if (dropdown === DropdownType.Month) {
        scrollVal = (this.currentDateView.next.month - 1) * 34;
        if (toggle) {
          this.showDropDownMonth = !this.showDropDownMonth;
          this.showDropDownYear = false;

          this.monthInputs.forEach(element => {
            if (element.nativeElement.className.indexOf('selected') > -1) {
              element.nativeElement.focus();
            }
          });
        }
      } else {
        scrollVal = 6 * 34;
        if (toggle) {
          this.showDropDownYear = !this.showDropDownYear;
          this.showDropDownMonth = false;

          this.yearInputs.forEach(element => {
            if (element.nativeElement.className.indexOf('selected') > -1) {
              element.nativeElement.focus();
            }
          });
        }
      }
      // Main scrollbar belongs to element with class name body.
      if (
        typeof document.getElementsByClassName(dropdown)[0].scroll !==
        'undefined'
      ) {
        document
          .getElementsByClassName(dropdown)[0]
          .scroll({ top: scrollVal, behavior: 'auto' });
      } else if (
        typeof document.getElementsByClassName(dropdown)[0].scrollTop !==
        'undefined'
      ) {
        document.getElementsByClassName(dropdown)[0].scrollTop = scrollVal;
      }
    }
  }

  // Convert object back to string.
  toDateString(value): string {
    // for instances with date ranges, the this.rangemodel is an array
    if (Array.isArray(value)) {
      return value
        ? `${isNumber(value[0].day) ? padNumber(value[0].day) : ''}/${
            isNumber(value[0].month) ? padNumber(value[0].month) : ''
          }/${value[0].year} - ${
            isNumber(value[1].day) ? padNumber(value[1].day) : ''
          }/${isNumber(value[1].month) ? padNumber(value[1].month) : ''}/${
            value[1].year
          }`
        : '';
    }
    return value
      ? `${isNumber(value.day) ? padNumber(value.day) : ''}/${
          isNumber(value.month) ? padNumber(value.month) : ''
        }/${value.year}`
      : '';
  }

  // convert Ngbstruct to Ngbdate
  toNgbdate(struct: any): NgbDate {
    return new NgbDate(struct.year, struct.month, struct.day);
  }

  // Handles date ranges
  onDateRangeSelection(date: NgbDate): void {
    let parsed = '';

    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      this.toDate = date;
      this.closeDatepicker();
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
    this.startDate = this.fromDate;

    // replace model and native element values with parsed value;
    if (this.fromDate) {
      parsed += this.toDateString(this.fromDate);
      this.rangeModel = ''; // set to empty string to prevent display of incomplete range
    }
    if (this.toDate) {
      parsed += ` - ${this.toDateString(this.toDate)}`;
      this.rangeModel = parsed;
      this.propagateChange(this.rangeModel);
      this.modelChange.emit(this.rangeModel);
      this.renderer.setProperty(
        this.input.nativeElement,
        'value',
        this.rangeModel
      );
      this.toggleButton.nativeElement.focus();
    }
  }

  isHovered(date: NgbDate): boolean {
    return (
      this.fromDate &&
      !this.toDate &&
      this.hoveredDate &&
      date.after(this.fromDate) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate): boolean {
    return date.after(this.fromDate) && date.before(this.toDate);
  }

  isActiveElement(element: Element): boolean {
    return document.activeElement === element;
  }

  isRange(date: NgbDate): boolean {
    return (
      date.equals(this.fromDate) ||
      date.equals(this.toDate) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  // Check if date selected is within range of min date and max date.
  public dateIsNotInRange(date: string | NgbDate): boolean {
    let result = false;

    if (typeof date === 'string') {
      date = this.splitDateStringToNgbDate(date);
    } else {
      date = NgbDate.from(date);
    }

    if (this.options.minDate) {
      const minDate = this.splitDateStringToNgbDate(this.options.minDate);

      if (date && minDate) {
        result = date.before(minDate);
      }
    }

    if (result === false && this.options.maxDate) {
      const maxDate = this.splitDateStringToNgbDate(this.options.maxDate);

      if (date && maxDate) {
        result = date.after(maxDate);
      }
    }

    if (date && result === false && this.options.markDisabledRules) {
      result = this.options.markDisabledRules(date);
    }

    return result;
  }

  getCurrentMonth(): string {
    return this.i18n.getMonthShortName(this.currentDateView.next.month);
  }

  getMonth(index: number): string {
    return this.i18n.getMonthShortName(index);
  }

  // Handles focusing away from datepicker.
  private registerListener(): void {
    this.listener = this.renderer.listen('document', 'click', (event: any) => {
      if (
        (this.model === null && this.rangeModel === null) ||
        this.dateIsNotInRange(this.model)
      ) {
        this.input.nativeElement.value = '';
      }
      if (!this.el.nativeElement.contains(event.target)) {
        this.closeDatepicker();
        this.listener();
      }
    });
  }

  private initModel(modelChanges = null): void {
    if (typeof this.model !== 'string') {
      this.model = this.toDateString(this.model);
    }

    if (this.options.readonly) {
      return;
    }

    const modelFormat = ['D/MM/YYYY', 'DD/MM/YYYY', 'D/M/YYYY', 'DD/MM/YYYY'];

    const modelRangeFormat = [
      'D/MM/YYYY - D/MM/YYYY',
      'D/MM/YYYY - DD/MM/YYYY',
      'DD/MM/YYYY - DD/MM/YYYY',
      'DD/MM/YYYY - D/MM/YYYY',
      'D/M/YYYY - D/M/YYYY',
      'D/M/YYYY - DD/M/YYYY',
      'DD/M/YYYY - D/M/YYYY',
      'DD/M/YYYY - DD/M/YYYY'
    ];
    const model = moment(this.model, modelFormat);
    const rangeModel = moment(this.rangeModel, modelRangeFormat);

    if (this.rangeModel && this.dateRange && this.rangeModel.includes('-')) {
      this.rangeModel = this.getDateRange(
        rangeModel,
        modelChanges,
        modelRangeFormat
      );
      return;
    }

    if (model.isValid()) {
      this.model = this.toDateObj(model.format('DD/MM/YYYY'));
    } else {
      if (!this.options.autoFillOnClear) {
        this.model = null;
      } else {
        this.model = modelChanges && modelChanges.previousValue
          ? this.toDateObj(modelChanges.previousValue)
          : this.toDateObj(moment().format('DD/MM/YYYY'));
      }
      setTimeout(() => this.modelChange.emit(this.model));
    }
  }

  private splitDateStringToNgbDate(date: string): NgbDate {
    const splitDate = date.split('/');
    return new NgbDate(+splitDate[2], +splitDate[1], +splitDate[0]);
  }
  private setInitialCurrentDateView(): void {
    if (
      this.options &&
      ((this.options.maxDate &&
        this.toDateObj(this.options.maxDate).after(this.calendar.getToday())) ||
        !this.options.maxDate)
    ) {
      this.currentDateView.next = this.calendar.getToday();
    } else {
      this.currentDateView.next = this.toDateObj(this.options.maxDate);
    }
  }
}
